import type { BotsConfig } from "./types";

// The patch a Save on the Bots page sends.
//
// `PATCH /projects/:id/bots/settings` takes an RFC 7386 merge patch, and this
// is what builds one out of the two documents a form holds: the server's answer
// and the draft laid over it.
//
// A diff rather than the whole document, and the reason is a deletion. RFC 7386
// merges objects key by key, so a document sent whole cannot REMOVE anything
// from `locale_by_country` or from `names.custom` — the two free-form maps on
// this page. An operator who deleted a locale would press Save, be told it
// saved, and find the locale still there. The patch says `null` for those keys,
// which is the RFC's own way of spelling a deletion, and nothing else has to
// know they are maps.

/**
 * A merge patch over the bot settings document.
 *
 * Keyed by `BotsConfig`'s own keys so a typo — `{enable: true}` — is a compile
 * error rather than a request the server accepts and stores beside the key that
 * was meant. The values stay loose: a patch names a sub-tree, a scalar, or
 * `null` for a deletion, and none of those is the field's own type.
 */
export type BotSettingsPatch = Partial<Record<keyof BotsConfig, unknown>>;

/** A JSON object and not an array — arrays replace wholesale under RFC 7386. */
function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Both sides come out of `normalizeBots`, which builds every object literally,
 * so two equal values are always written in the same key order and a string
 * comparison is exact. It is the same test `projects/config/utils.ts` makes on
 * the same kind of value, for the same reason.
 */
function isEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * mergePatch describes how to get from `from` to `to`.
 *
 * Keys that agree are left out entirely, which is what keeps a save that
 * touched one number from carrying the whole balance sheet — and what lets the
 * source switch and the header's simulation toggle use the same endpoint with a
 * patch of one word.
 *
 * Arrays are replaced whole, deliberately: a profile table or a set of
 * time-of-day bands merged element by element would produce a table nobody
 * wrote.
 */
export function mergePatch(from: BotsConfig, to: BotsConfig): BotSettingsPatch {
  return patchOf(from, to) as BotSettingsPatch;
}

/**
 * The recursion. `undefined` is treated as absent on both sides, deliberately:
 * JSON has no such value, and a patch carrying one would be dropped by
 * JSON.stringify on the way out — an edit that vanished between the form and
 * the wire, with the draft cleared as though it had landed.
 */
function patchOf(from: unknown, to: unknown): unknown {
  if (!isPlainObject(from) || !isPlainObject(to)) return to;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(from)) {
    if (from[key] === undefined) continue;
    if (!(key in to) || to[key] === undefined) out[key] = null;
  }
  for (const [key, next] of Object.entries(to)) {
    if (next === undefined) continue;
    const had = key in from && from[key] !== undefined;
    if (!had) {
      out[key] = next;
      continue;
    }
    const prev = from[key];
    if (isEqual(prev, next)) continue;
    out[key] =
      isPlainObject(prev) && isPlainObject(next) ? patchOf(prev, next) : next;
  }
  return out;
}
